<template>
  <p v-if="locations.length > 0" class="text-brand font-bold mb-0">
    {{ $t('landingpage/orderwidget/dropdown/title_other_region') }}:
  </p>

  <!-- location switch -->
  <div ref="dropdownContainer" class="relative pb-2 -mx-2">
    <button
      v-if="selectedLocation"
      ref="dropdownToggle"
      :aria-expanded="isOpen"
      class="w-full relative border-b border-rhino px-2 py-1 truncate text-left font-normal flex justify-between items-center bg-transparent text-mouse"
      type="button"
      data-testid="country-selector-dropdown"
      @click="toggleDropdown"
    >
      <div class="flex-1 min-w-0 truncate">
        <common-flag-icon :iso-code="selectedLocation.countryCode" />
        {{ selectedLocation.country }}
        <span v-if="!isCentralEuropeLocation(selectedLocation)">
          ({{ selectedLocation.city }})
        </span>
      </div>
      <div class="w-auto flex items-center shrink-0">
        <client-only v-if="selectedLocation.available">
          <common-latency-result
            :slug="selectedLocation.slug"
            class="pl-3 mr-1 block"
          />
        </client-only>
        <fa-icon
          class="text-rhino size-3 transition-transform duration-200"
          :class="{ 'rotate-180': isOpen }"
          :icon="faChevronDown"
        />
      </div>
    </button>

    <div
      v-if="isOpen"
      class="absolute z-10 min-w-full bg-depth-3 rounded-b border border-rhino/20 -mt-0.5"
    >
      <!-- Sold-out rows stay focusable and announced (aria-disabled, not
           disabled), and selectLocation ignores them. -->
      <button
        v-for="location in sortedLocations"
        :key="location.slug"
        type="button"
        :aria-disabled="!location.available"
        :data-testid="testId([location.slug, 'location-btn'])"
        class="w-full px-2 py-1 flex flex-row justify-between items-center whitespace-nowrap overflow-visible text-left font-normal bg-transparent"
        :class="{
          'cursor-pointer text-mouse no-underline hover:text-white focus-visible:text-white':
            location.available,
          'cursor-default': !location.available,
        }"
        @click="() => selectLocation(location)"
      >
        <span
          :class="{
            'line-through': !location.available,
          }"
        >
          <common-flag-icon :iso-code="location.countryCode" />
          {{ location.country }}
          <span v-if="isCentralEuropeLocation(location)"> * </span>
          <span v-else> ({{ location.city }}) </span>
        </span>

        <client-only v-if="location.available">
          <common-latency-result :slug="location.slug" class="pl-3" />
        </client-only>
        <span v-else>
          {{ $t('landingpage/orderwidget/dropdown/sold_out') }}
        </span>
      </button>
      <template v-if="centralEuropeLocation != null">
        <hr class="my-1 border-rhino/20" />
        <div
          class="px-2 py-1 flex flex-row justify-between items-center whitespace-normal"
        >
          * {{ centralEuropeLocation.city }}
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { onClickOutside } from '@vueuse/core';
import type { StrapiLocalizedProductConfiguration } from '~/apollo/types/types';

import testId from '~/helpers/testid';

const emit = defineEmits<{
  (e: 'locationSelected', val: StrapiLocalizedProductConfiguration): void;
}>();

const props = defineProps<{
  locations: StrapiLocalizedProductConfiguration[];
  selectedLocation?: StrapiLocalizedProductConfiguration;
}>();

const locationStore = useLocationStore();
const latencies = computed<LatencyMeasurement[]>(
  () => locationStore.latencyMeasurements,
);

const sortedLocations = ref<StrapiLocalizedProductConfiguration[]>(
  props.locations,
);

const sortLocations = (input: StrapiLocalizedProductConfiguration[]) => {
  const orderLocationsSorted = [...input];
  if (
    import.meta.client &&
    latencies.value != null &&
    latencies.value.length > 0
  ) {
    orderLocationsSorted.sort(
      (
        a: StrapiLocalizedProductConfiguration,
        b: StrapiLocalizedProductConfiguration,
      ) => {
        const aLatency = latencies.value.find((l) => l.slug === a.slug);
        const bLatency = latencies.value.find((l) => l.slug === b.slug);
        if (aLatency && bLatency) {
          return aLatency.latency < bLatency.latency ? -1 : 1;
        }

        return input.length + 1;
      },
    );
  } else {
    orderLocationsSorted.sort(
      (
        a: StrapiLocalizedProductConfiguration,
        b: StrapiLocalizedProductConfiguration,
      ) => {
        if (a.country === b.country) {
          return a.city > b.city ? 1 : -1;
        }
        return a.country > b.country ? 1 : -1;
      },
    );
  }

  sortedLocations.value = orderLocationsSorted;
};

const isCentralEuropeLocation = (
  location: StrapiLocalizedProductConfiguration,
) => location.key === 'EUW';

const centralEuropeLocation = computed<StrapiLocalizedProductConfiguration>(
  () => {
    return props.locations.find((l) => l.key === 'EUW');
  },
);

watchDebounced(
  () => latencies.value,
  () => sortLocations(props.locations),
  { deep: true, debounce: 100 },
);

const dropdownContainer = ref<HTMLElement>(null);
const isOpen = ref(false);

const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
};

const selectLocation = (location: StrapiLocalizedProductConfiguration) => {
  // Sold-out locations are not selectable: clicking one is a no-op and leaves
  // the menu open so another region can be picked.
  if (!location.available) {
    return;
  }

  emit('locationSelected', location);
  isOpen.value = false;
};

onClickOutside(dropdownContainer, () => {
  isOpen.value = false;
});

onMounted(async () => {
  setTimeout(() => {
    sortLocations(props.locations);
  }, 100);
});
</script>
